export type LocationLabelSourceRow = {
  display_name?: string | null;
  entity_identifier?: string | null;
};

export const buildLocationLabel = (row: LocationLabelSourceRow): string => {
  const name = row.display_name?.trim();
  if (name) {
    return name;
  }

  const identifier = row.entity_identifier?.trim();
  if (identifier) {
    return identifier;
  }

  return 'Unnamed Location';
};

export const splitLocationBreadcrumb = (breadcrumb: string): string[] => {
  const normalized = breadcrumb.trim();
  if (!normalized) {
    return [];
  }

  const delimiter = normalized.includes(' / ') ? ' / ' : normalized.includes(' > ') ? ' > ' : '/';

  return normalized
    .split(delimiter)
    .map((part) => part.trim())
    .filter(Boolean);
};

export const normalizeLocationBreadcrumb = (breadcrumb: string): string =>
  splitLocationBreadcrumb(breadcrumb).join(' / ');

const computeCommonPrefixLength = (paths: string[][]): number => {
  if (paths.length === 0) {
    return 0;
  }

  const minLen = Math.min(...paths.map((p) => p.length));
  let prefixLen = 0;

  for (let i = 0; i < minLen; i += 1) {
    const part = paths[0]?.[i];
    if (!part) {
      break;
    }

    const allMatch = paths.every((p) => (p[i] ?? '').toLowerCase() === part.toLowerCase());
    if (!allMatch) {
      break;
    }

    prefixLen += 1;
  }

  return prefixLen;
};

export const computeCommonBreadcrumbPrefixLen = (
  options: Array<{ breadcrumb?: string | null }>,
): number => {
  const paths = options
    .map((o) => (o.breadcrumb ? splitLocationBreadcrumb(o.breadcrumb) : []))
    .filter((segments) => segments.length > 0);

  const prefixLen = computeCommonPrefixLength(paths);
  return prefixLen >= 1 ? prefixLen : 0;
};

export const formatLocationOptionLabel = (
  option: { label: string; breadcrumb?: string | null },
  commonPrefixLen: number,
): string => {
  const leaf = option.label?.trim() ?? '';
  const breadcrumb = option.breadcrumb?.trim() ?? '';

  if (!breadcrumb) {
    return leaf;
  }

  const segments = splitLocationBreadcrumb(breadcrumb);
  const trimmed = segments.slice(commonPrefixLen).join(' / ').trim();

  return trimmed || leaf || breadcrumb;
};

export const filterLeafLocationOptions = <T extends { id: string; breadcrumb?: string | null }>(
  options: T[],
): T[] => {
  const normalizedById = new Map<string, string>();
  const normalizedPaths: string[] = [];

  for (const option of options) {
    const breadcrumb = option.breadcrumb?.trim();
    if (!breadcrumb) {
      continue;
    }

    const normalized = normalizeLocationBreadcrumb(breadcrumb);
    normalizedById.set(option.id, normalized);
    normalizedPaths.push(normalized);
  }

  if (normalizedPaths.length === 0) {
    return options;
  }

  return options.filter((option) => {
    const normalized = normalizedById.get(option.id);
    if (!normalized) {
      // If no breadcrumb, keep it (typically already a leaf, or legacy data).
      return true;
    }

    const prefix = `${normalized} / `;
    const hasChild = normalizedPaths.some((path) => path !== normalized && path.startsWith(prefix));
    return !hasChild;
  });
};
